/**
 * Breadcrumbs for the Files browser.
 *
 * The current directory, split into clickable segments. Each segment is a
 * navigation target AND a drop target: dragging a row onto a breadcrumb moves
 * the file into that ancestor folder, which is the Finder gesture for "put this
 * back up the tree".
 */

import type { DragEvent, ReactElement } from 'react';
import './BreadcrumbsComponent.css';

export interface BreadcrumbsComponentProps {
  /** Absolute VFS path of the current directory, e.g. '/library/notes/2026'. */
  dir: string;
  /** Human label for each mount root, keyed by mount path (e.g. '/library' → 'Library'). */
  mountLabels: Record<string, string>;
  /** Navigate to an ancestor directory. */
  onNavigate: (dir: string) => void;
  /** Move a dragged entry into the folder this segment represents. */
  onDropMove: (fromPaths: string[], toDir: string) => void;
  /** Whether a drag is in progress anywhere in the browser, to show drop affordance. */
  dragActive?: boolean;
}

export function BreadcrumbsComponent({
  dir,
  mountLabels,
  onNavigate,
  onDropMove,
  dragActive,
}: BreadcrumbsComponentProps): ReactElement {
  const segments = dir.split('/').filter(Boolean);
  // Reconstruct absolute paths segment by segment so each crumb knows its target.
  const crumbs: Array<{ label: string; path: string }> = [];
  let acc = '';
  for (const seg of segments) {
    acc += `/${seg}`;
    // The first segment is a mount root; use its human label rather than '/library'.
    const label = crumbs.length === 0 ? (mountLabels[acc] ?? seg) : seg;
    crumbs.push({ label, path: acc });
  }

  const handleDrop = (e: DragEvent<HTMLButtonElement>, toDir: string) => {
    e.preventDefault();
    e.currentTarget.classList.remove('file-crumb--drag-over');
    // Prefer the multi-path payload, fall back to the single path.
    const raw = e.dataTransfer.getData('text/vfs-paths') || e.dataTransfer.getData('text/vfs-path');
    if (!raw) return;
    const fromPaths = raw.split('\n').filter((p) => p && p !== toDir && !p.startsWith(`${toDir}/`));
    if (fromPaths.length > 0) onDropMove(fromPaths, toDir);
  };

  return (
    <nav className="file-crumbs" aria-label="File path">
      {crumbs.length === 0 && <span className="file-crumbs-root">Files</span>}
      {crumbs.map((c, i) => (
        <span key={c.path} className="file-crumb-slot">
          {i > 0 && <span className="file-crumb-sep" aria-hidden="true">›</span>}
          <button
            type="button"
            className={`file-crumb${dragActive ? ' file-crumb--droppable' : ''}`}
            onClick={() => onNavigate(c.path)}
            onDragOver={(e) => { if (dragActive && (e.dataTransfer.types.includes('text/vfs-paths') || e.dataTransfer.types.includes('text/vfs-path'))) e.preventDefault(); }}
            onDragEnter={(e) => { if (dragActive) e.currentTarget.classList.add('file-crumb--drag-over'); }}
            onDragLeave={(e) => e.currentTarget.classList.remove('file-crumb--drag-over')}
            onDrop={(e) => handleDrop(e, c.path)}
          >
            {c.label}
          </button>
        </span>
      ))}
    </nav>
  );
}
